import { Suspense } from "react";
import { useAtomValue } from "jotai";
import {
  agentAtomFamily_SUSPENDS,
  AgentFetchResult,
} from "@zapier/canvas-state";
import { BotAvatar } from "./BotAvatar";
import { AssetIcon } from "../AssetIcon";
import styles from "./AgentBotAvatar.module.css";

type Props = {
  agentId: string;
};

/**
 * Component that fetches and displays the agent avatar
 * Uses suspense for data loading
 */
function SuspendedAgentBotAvatar({ agentId }: Props) {
  // Fetch agent data using the same atom as AgentPreview
  const agentResult = useAtomValue(agentAtomFamily_SUSPENDS(agentId)) as
    | AgentFetchResult
    | null
    | undefined;

  // Differentiate result states
  if (
    !agentResult ||
    agentResult.type === "not_found" ||
    agentResult.type === "forbidden"
  ) {
    return <AssetIcon iconName="navCentral" />;
  }

  // Success case
  if (agentResult.type === "success") {
    return (
      <div className={styles.botAvatarWrapper}>
        <BotAvatar agentAvatar={agentResult.data.avatar} />
      </div>
    );
  }

  // Fallback: shouldn't reach here, but render generic icon
  return <AssetIcon iconName="navCentral" />;
}

/**
 * AgentBotAvatar component for AssetNodeSkeleton footer
 * Displays the agent avatar when available
 */
export function AgentBotAvatar({ agentId }: Props) {
  if (!agentId) {
    return null;
  }

  return (
    <Suspense fallback={<AssetIcon iconName="navCentral" />}>
      <SuspendedAgentBotAvatar agentId={agentId} />
    </Suspense>
  );
}
